/**
 * ============================================================================
 * ADVANCED CRITERIA EVALUATION UTILITIES
 * ============================================================================
 * Evaluation logic for conditional criteria with IF-THEN-ELSE logic,
 * AND/OR operators, and nested rules.
 */

// ============================================================================
// SINGLE CONDITION EVALUATION
// ============================================================================

/**
 * Evaluate a single condition against a field value
 * 
 * @param {Object} condition - The condition to evaluate
 * @param {any} fieldValue - The actual field value
 * @returns {boolean} Whether the condition is met
 */
export const evaluateSingleCondition = (condition, fieldValue) => {
  const { operator, value } = condition;
  
  if (fieldValue === undefined || fieldValue === null || fieldValue === '') {
    return false;
  }
  
  switch (operator) {
    case '=':
      return String(fieldValue).toLowerCase() === String(value).toLowerCase();
      
    case 'is_one_of': {
      const options = Array.isArray(value) ? value : [value];
      return options.some(opt => String(fieldValue).toLowerCase() === String(opt).toLowerCase());
    }
    
    case 'is_not': {
      const excluded = Array.isArray(value) ? value : [value];
      return !excluded.some(opt => String(fieldValue).toLowerCase() === String(opt).toLowerCase());
    }
    
    case '>':
      return parseFloat(normalizeNumericValue(fieldValue)) > parseFloat(normalizeNumericValue(value));
      
    case '>=':
      return parseFloat(normalizeNumericValue(fieldValue)) >= parseFloat(normalizeNumericValue(value));
      
    case '<':
      return parseFloat(normalizeNumericValue(fieldValue)) < parseFloat(normalizeNumericValue(value));
      
    case '<=':
      return parseFloat(normalizeNumericValue(fieldValue)) <= parseFloat(normalizeNumericValue(value));
      
    case 'contains':
      return String(fieldValue).toLowerCase().includes(String(value).toLowerCase());
      
    case 'older_than_months': {
      if (!fieldValue) return false;
      const dateValue = new Date(fieldValue);
      if (isNaN(dateValue.getTime())) return false;
      const now = new Date();
      const monthsDiff = (now.getFullYear() - dateValue.getFullYear()) * 12 + 
                        (now.getMonth() - dateValue.getMonth());
      return monthsDiff >= parseInt(value, 10);
    }
    
    default:
      return false;
  }
};

/**
 * Normalize a value to a number
 * 
 * @param {any} value - The value to normalize
 * @returns {number} Normalized numeric value
 */
const normalizeNumericValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return 0;
  }
  
  // Remove any non-numeric characters except decimal point and minus
  const cleanValue = String(value).replace(/[^0-9.-]/g, '');
  const parsedValue = parseFloat(cleanValue);
  
  return isNaN(parsedValue) ? 0 : parsedValue;
};

// ============================================================================
// CONDITION GROUP EVALUATION (AND/OR with nesting)
// ============================================================================

/**
 * Evaluate a condition group recursively (supports AND/OR with nesting)
 * 
 * @param {Object} conditionGroup - The condition group to evaluate
 * @param {Function} getFieldValue - Function to get field values: (fieldId) => value
 * @returns {boolean} Whether the condition group is satisfied
 */
export const evaluateConditionGroup = (conditionGroup, getFieldValue) => {
  if (!conditionGroup) return true;
  
  const { logic, conditions = [], groups = [] } = conditionGroup;
  
  // Evaluate all leaf conditions
  const conditionResults = conditions.map(condition => {
    const fieldValue = getFieldValue(condition.fieldId);
    return evaluateSingleCondition(condition, fieldValue);
  });
  
  // Evaluate all nested groups recursively
  const groupResults = groups.map(group => 
    evaluateConditionGroup(group, getFieldValue)
  );
  
  // Combine all results
  const allResults = [...conditionResults, ...groupResults];
  
  if (allResults.length === 0) return true;
  
  // Apply AND/OR logic
  if (logic === 'AND') {
    return allResults.every(result => result === true);
  } else {  // OR
    return allResults.some(result => result === true);
  }
};

// ============================================================================
// CONDITIONAL RULE EVALUATION
// ============================================================================

/**
 * Evaluate a single conditional rule
 * 
 * @param {Object} rule - The conditional rule to evaluate
 * @param {Function} getFieldValue - Function to get field values
 * @param {Function} evaluateCriterion - Function to evaluate a single criterion
 * @param {Object} parentContext - Optional parent context for nested rules
 * @returns {Object} Evaluation results for this rule
 */
const evaluateConditionalRule = (rule, getFieldValue, evaluateCriterion, parentContext = null) => {
  const results = {
    ruleId: rule.id,
    ruleName: rule.name,
    conditionsMet: false,
    branch: null,
    matched: [],
    missed: [],
    incomplete: [],
    disqualified: false,
    totalWeight: 0,
    weightedScore: 0,
    nestedResults: []
  };
  
  if (!rule.enabled) {
    return results;
  }
  
  // Step 1: Evaluate the IF condition group
  const conditionsMet = evaluateConditionGroup(rule.conditionGroup, getFieldValue);
  results.conditionsMet = conditionsMet;
  results.branch = conditionsMet ? 'THEN' : 'ELSE';
  
  // Build context description for display
  const conditionContext = {
    ruleId: rule.id,
    ruleName: rule.name,
    conditionsMet,
    conditionDescription: formatConditionDescription(rule.conditionGroup),
    branch: results.branch,
    parentContext
  };
  
  // Step 2: Determine which criteria to evaluate
  const criteriaToEvaluate = conditionsMet 
    ? (rule.thenCriteria || [])
    : (rule.elseCriteria || []);
  
  // Step 3: Evaluate criteria
  criteriaToEvaluate.forEach(criterion => {
    const fieldValue = getFieldValue(criterion.fieldId);
    const isIncomplete = fieldValue === undefined || fieldValue === null || fieldValue === '';
    const weight = criterion.weight || 5;
    
    const evaluatedCriterion = {
      ...criterion,
      actualValue: fieldValue,
      conditionalContext: conditionContext
    };
    
    results.totalWeight += weight;
    
    if (isIncomplete && criterion.required) {
      results.incomplete.push(evaluatedCriterion);
    } else if (isIncomplete) {
      // Non-required incomplete - counts as not met but doesn't disqualify
      results.missed.push(evaluatedCriterion);
    } else {
      const isMet = evaluateCriterion(criterion, fieldValue);
      
      if (isMet) {
        results.matched.push(evaluatedCriterion);
        results.weightedScore += weight;
      } else {
        results.missed.push(evaluatedCriterion);
        
        if (criterion.required || rule.required) {
          results.disqualified = true;
        }
      }
    }
  });
  
  // Step 4: Evaluate nested rules (only if parent condition was met)
  if (conditionsMet && rule.nestedRules && rule.nestedRules.length > 0) {
    rule.nestedRules.forEach(nestedRule => {
      const nestedResult = evaluateConditionalRule(
        nestedRule, 
        getFieldValue, 
        evaluateCriterion, 
        conditionContext
      );
      results.nestedResults.push(nestedResult);
      
      // Aggregate nested results
      results.matched.push(...nestedResult.matched);
      results.missed.push(...nestedResult.missed);
      results.incomplete.push(...nestedResult.incomplete);
      results.totalWeight += nestedResult.totalWeight;
      results.weightedScore += nestedResult.weightedScore;
      
      if (nestedResult.disqualified) {
        results.disqualified = true;
      }
    });
  }
  
  return results;
};

// ============================================================================
// MAIN EVALUATION FUNCTION
// ============================================================================

/**
 * Evaluate all advanced criteria for a lender
 * 
 * @param {Object} lenderCriteria - The lender's advanced criteria
 * @param {Function} getFieldValue - Function to get field values: (fieldId) => value
 * @param {Function} evaluateCriterion - Function to evaluate a single criterion (from existing code)
 * @returns {Object} Complete evaluation results
 */
export const evaluateAdvancedCriteria = (lenderCriteria, getFieldValue, evaluateCriterion) => {
  const results = {
    // Simple criteria results
    simpleMatched: [],
    simpleMissed: [],
    simpleIncomplete: [],
    
    // Conditional criteria results (grouped separately when conditions pass)
    conditionalMatched: [],
    conditionalMissed: [],
    conditionalIncomplete: [],
    
    // Skipped conditional rules (condition not met, no ELSE criteria)
    skippedRules: [],
    
    // Aggregated stats
    totalWeight: 0,
    weightedScore: 0,
    disqualified: false,
    disqualifiedBySimple: false,
    disqualifiedByConditional: false,
    
    // Detailed rule results for debugging/display
    ruleResults: []
  };
  
  const { simpleCriteria = [], conditionalRules = [] } = lenderCriteria;
  
  // ========================================
  // Step 1: Evaluate simple criteria
  // ========================================
  simpleCriteria.forEach(criterion => {
    const fieldValue = getFieldValue(criterion.fieldId);
    const isIncomplete = fieldValue === undefined || fieldValue === null || fieldValue === '';
    const weight = criterion.weight || 5;
    
    const evaluatedCriterion = {
      ...criterion,
      actualValue: fieldValue,
      isSimple: true
    };
    
    results.totalWeight += weight;
    
    if (isIncomplete && criterion.required) {
      results.simpleIncomplete.push(evaluatedCriterion);
    } else if (isIncomplete) {
      results.simpleMissed.push(evaluatedCriterion);
    } else {
      const isMet = evaluateCriterion(criterion, fieldValue);
      
      if (isMet) {
        results.simpleMatched.push(evaluatedCriterion);
        results.weightedScore += weight;
      } else {
        results.simpleMissed.push(evaluatedCriterion);
        
        if (criterion.required) {
          results.disqualified = true;
          results.disqualifiedBySimple = true;
        }
      }
    }
  });
  
  // ========================================
  // Step 2: Evaluate conditional rules
  // ========================================
  console.log(`🔀 Evaluating ${conditionalRules.length} conditional rules`);
  
  conditionalRules.forEach((rule, index) => {
    console.log(`🔀 Rule ${index + 1}: "${rule.name}", enabled: ${rule.enabled}`);
    
    if (!rule.enabled) {
      console.log(`  ⏭️ Skipping disabled rule`);
      return;
    }
    
    console.log(`  📋 Condition group:`, JSON.stringify(rule.conditionGroup));
    console.log(`  📋 THEN criteria:`, rule.thenCriteria?.length || 0);
    console.log(`  📋 ELSE criteria:`, rule.elseCriteria?.length || 0);
    
    const ruleResult = evaluateConditionalRule(rule, getFieldValue, evaluateCriterion);
    
    console.log(`  ✅ Rule result:`, {
      conditionsMet: ruleResult.conditionsMet,
      branch: ruleResult.branch,
      matched: ruleResult.matched?.length || 0,
      missed: ruleResult.missed?.length || 0,
      incomplete: ruleResult.incomplete?.length || 0,
      totalWeight: ruleResult.totalWeight
    });
    
    results.ruleResults.push(ruleResult);
    
    // Check if this rule was "skipped" (condition not met AND no ELSE criteria evaluated)
    const hasEvaluatedCriteria = ruleResult.matched.length > 0 || 
                                  ruleResult.missed.length > 0 || 
                                  ruleResult.incomplete.length > 0;
    
    if (!ruleResult.conditionsMet && !hasEvaluatedCriteria) {
      // Rule was skipped because condition wasn't met and no ELSE criteria
      results.skippedRules.push({
        ruleId: rule.id,
        ruleName: rule.name,
        conditionDescription: formatConditionDescription(rule.conditionGroup),
        reason: 'Condition not met',
        thenCriteriaCount: rule.thenCriteria?.length || 0
      });
      console.log(`  ⏭️ Rule skipped (condition not met, no ELSE criteria)`);
    }
    
    // Aggregate results
    results.conditionalMatched.push(...ruleResult.matched);
    results.conditionalMissed.push(...ruleResult.missed);
    results.conditionalIncomplete.push(...ruleResult.incomplete);
    
    results.totalWeight += ruleResult.totalWeight;
    results.weightedScore += ruleResult.weightedScore;
    
    if (ruleResult.disqualified) {
      results.disqualified = true;
      results.disqualifiedByConditional = true;
    }
  });
  
  // ========================================
  // Step 3: Calculate final score
  // ========================================
  results.score = results.totalWeight > 0 
    ? Math.round((results.weightedScore / results.totalWeight) * 100) 
    : 0;
  
  if (results.disqualified) {
    results.score = 0;
  }
  
  // Auto-decline if any required criteria are missed (not incomplete)
  results.autoDecline = results.simpleMissed.some(c => c.required) || 
                        results.conditionalMissed.some(c => c.required);
  
  return results;
};

// ============================================================================
// FORMATTING UTILITIES
// ============================================================================

/**
 * Format a condition group as a human-readable string
 * 
 * @param {Object} conditionGroup - The condition group
 * @returns {string} Human-readable description
 */
export const formatConditionDescription = (conditionGroup) => {
  if (!conditionGroup) return '';
  
  const { logic, conditions = [], groups = [] } = conditionGroup;
  
  const operatorLabels = {
    '=': 'is',
    'is_one_of': 'is one of',
    'is_not': 'is not',
    '>': '>',
    '>=': '≥',
    '<': '<',
    '<=': '≤',
    'contains': 'contains',
    'older_than_months': 'older than (months)'
  };
  
  const conditionTexts = conditions.map(c => {
    const opText = operatorLabels[c.operator] || c.operator;
    const valueText = Array.isArray(c.value) ? `[${c.value.join(', ')}]` : c.value;
    return `${c.fieldName || c.fieldId} ${opText} ${valueText}`;
  });
  
  const groupTexts = groups.map(g => `(${formatConditionDescription(g)})`);
  
  const allTexts = [...conditionTexts, ...groupTexts];
  const connector = logic === 'AND' ? ' AND ' : ' OR ';
  
  return allTexts.join(connector) || 'No conditions';
};

/**
 * Format a criterion for display
 * 
 * @param {Object} criterion - The criterion to format
 * @returns {string} Human-readable description
 */
export const formatCriterionDescription = (criterion) => {
  const operatorLabels = {
    '>': 'greater than',
    '>=': 'at least',
    '=': 'equals',
    '<=': 'at most',
    '<': 'less than',
    'contains': 'contains',
    'is_not': 'is not',
    'is_one_of': 'is one of',
    'older_than_months': 'older than (months)'
  };
  
  const opText = operatorLabels[criterion.operator] || criterion.operator;
  const valueText = Array.isArray(criterion.value) 
    ? `[${criterion.value.join(', ')}]` 
    : criterion.value;
  
  return `${criterion.fieldName || criterion.fieldId} ${opText} ${valueText}`;
};

/**
 * Group evaluated criteria by their conditional rule
 * 
 * @param {Array} criteria - Array of evaluated criteria with conditionalContext
 * @returns {Array} Array of groups, each with ruleId, ruleName, conditionDescription, and criteria
 */
export const groupCriteriaByRule = (criteria) => {
  const groups = {};
  const ungrouped = [];
  
  criteria.forEach(criterion => {
    if (criterion.conditionalContext) {
      const ruleId = criterion.conditionalContext.ruleId;
      if (!groups[ruleId]) {
        groups[ruleId] = {
          ruleId,
          ruleName: criterion.conditionalContext.ruleName,
          conditionDescription: criterion.conditionalContext.conditionDescription,
          conditionsMet: criterion.conditionalContext.conditionsMet,
          branch: criterion.conditionalContext.branch,
          criteria: []
        };
      }
      groups[ruleId].criteria.push(criterion);
    } else {
      ungrouped.push(criterion);
    }
  });
  
  return {
    grouped: Object.values(groups),
    ungrouped
  };
};

